import './bugsnag'
import './pe-carousel'
import './pe-accordion'
import './pe-inline-spoiler'
import './pe-code-highlight'
import Bugsnag from '@bugsnag/js'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

// Real-time broadcasting via self-hosted Reverb (Pusher-protocol compatible).
// Wired up only if a key is configured so dev/staging without Reverb still boot.
if (import.meta.env.VITE_REVERB_APP_KEY) {
    window.Pusher = Pusher
    window.Echo = new Echo({
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: import.meta.env.VITE_REVERB_PORT ?? 80,
        wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
        forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
        enabledTransports: ['ws', 'wss'],
    })
}

// Make Bugsnag available globally for console testing and manual error reporting
window.Bugsnag = Bugsnag

// Set user context if available (also set in bugsnag.js onError, but this ensures it's set early)
if (window.Laravel && window.Laravel.user) {
    Bugsnag.setUser(window.Laravel.user.id, window.Laravel.user.email, window.Laravel.user.name)
}

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', event => {
    Bugsnag.notify(event.reason, function(report) {
        report.severity = 'error'
        report.addMetadata('promise', {
            type: 'unhandledrejection',
            reason: event.reason?.message || String(event.reason),
        })
    })
})

// Handle global errors
window.addEventListener('error', event => {
    // Don't double-report if Bugsnag already caught it
    if (event.error && event.error.__bugsnag_handled__) {
        return
    }

    // A cross-origin script that threw without CORS headers, or a browser
    // extension, gives us a bare "Script error." with no error object, no file
    // and no line. There is nothing to act on, and reporting it anyway files
    // every such incident under this handler's own line, which buries the real
    // errors. Drop it instead.
    if (!event.error && (!event.filename || event.message === 'Script error.')) {
        return
    }

    Bugsnag.notify(event.error || new Error(event.message), function(report) {
        report.severity = 'error'
        report.addMetadata('error', {
            type: 'window.onerror',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        })
    })
})

// Track Livewire errors if Livewire is present
document.addEventListener('livewire:init', () => {
    if (window.Livewire) {
        // Track Livewire navigation
        Livewire.hook('request', ({ component, commit, respond, succeed, fail }) => {
            const componentName = component?.name || component?.fingerprint?.name || 'unknown'

            Bugsnag.leaveBreadcrumb('Livewire request', {
                component: componentName,
            }, 'request')

            fail(({ status, content }) => {
                Bugsnag.notify(new Error(`Livewire request failed: ${status}`), function(report) {
                    report.severity = 'error'
                    report.addMetadata('livewire', {
                        component: componentName,
                        status: status,
                    })
                })
            })
        })
    }
})

// Track AJAX/Fetch errors
const originalFetch = window.fetch
window.fetch = async function(...args) {
    const url = args[0]?.url || args[0]
    Bugsnag.leaveBreadcrumb('Fetch request', { url }, 'request')

    try {
        const response = await originalFetch.apply(this, args)

        if (!response.ok && response.status >= 400) {
            Bugsnag.leaveBreadcrumb('Fetch error response', {
                url,
                status: response.status,
                statusText: response.statusText,
            }, 'request')
        }

        return response
    } catch (error) {
        // Don't report failed beacons to third-party analytics/tracking
        // endpoints. These "Failed to fetch" errors are caused by ad/tracker
        // blockers or DNS filters on the visitor's device, not by us — the
        // request never even reaches our server.
        let isFirstParty = true
        try {
            isFirstParty = new URL(url, window.location.href).origin === window.location.origin
        } catch {
            // Relative or malformed URL — treat as first-party so we don't lose real errors.
            isFirstParty = true
        }

        if (isFirstParty) {
            Bugsnag.notify(error, function(report) {
                report.addMetadata('fetch', { url })
            })
        }
        throw error
    }
}
